/**
 * SPEC-annotation-review-concurrency Phase 5 — sibling hook to
 * useQaWebSocket for the annotation-review claim namespace.
 *
 * Why a separate hook (not an extension of useQaWebSocket): the QA flow
 * and the annotation review flow have different lifecycle assumptions.
 * useQaWebSocket is mounted on the QA list / detail pages and tracks
 * agent-claim semantics; this hook is mounted only when the page is in
 * review mode and tracks the orthogonal review-claim namespace. Sharing
 * the existing hook would entangle two unrelated event surfaces; a sibling
 * keeps each concern testable on its own. (Both use the same socket
 * connection — both connect to the singleton Engine.IO transport at
 * /socket.io — so there is one connection per page no matter how many
 * hooks are mounted.)
 *
 * Surface (mirrors useQaWebSocket exactly so the qa-detail-v3 page can copy
 * its pattern):
 *
 *   const {
 *     connectionState,           // 'connecting' | 'connected' | 'disconnected' | 'error'
 *     activeClaim,               // {claimerId, claimerName, claimedAt} | null — last seen claim per server, locally tracked
 *     claimReview,               // (deliveryId, opts?) → emits review:claim
 *     releaseReview,             // (deliveryId) → emits review:release
 *   } = useAnnotationReviewSocket(userId, onReviewUpdate, userInfo);
 *
 * Lifecycle:
 *   - Mount → connect socket, emit qa:auth (re-using the existing auth
 *     event since auth state is shared between QA and review namespaces).
 *   - Unmount → disconnect socket. The server's disconnect handler is the
 *     backstop: any review claim this socket held auto-releases.
 *   - 30-second heartbeat pings the same `qa:heartbeat` event the QA flow
 *     already pumps — no per-delivery interval, no new server work.
 *
 * Events the hook subscribes to:
 *   - review:update           — broadcast event; passed through to onReviewUpdate
 *   - review:claimed          — ack to a claim emit; updates internal state
 *   - review:claim_failed     — denial; passed through (component renders banner)
 *   - review:released         — ack to a release emit
 *   - review:error            — server-side error; logged to console
 */
import { useEffect, useRef, useCallback, useState } from "react";
import { io, Socket } from "socket.io-client";
import type {
  ReviewClaim,
  ReviewClaimedAck,
  ReviewClaimFailed,
  ReviewReleasedAck,
  ReviewUpdateEvent,
} from "@shared/annotation-review-claims";

type ConnectionState = "connecting" | "connected" | "disconnected" | "error";

export interface UseAnnotationReviewSocketReturn {
  connectionState: ConnectionState;
  /** Most recent claimed/claim_failed payload received for ANY deliveryId.
   * Components decide whether the payload is for the delivery they're
   * looking at. A claim/release on a different delivery does not clear
   * this. */
  lastClaimAck: ReviewClaimedAck | ReviewClaimFailed | null;
  claimReview: (deliveryId: string, opts?: { force?: boolean }) => void;
  releaseReview: (deliveryId: string) => void;
}

export function useAnnotationReviewSocket(
  userId: string | null,
  onReviewUpdate: (event: ReviewUpdateEvent) => void,
  userInfo?: { userName?: string; email?: string },
): UseAnnotationReviewSocketReturn {
  const socketRef = useRef<Socket | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>("disconnected");
  const [lastClaimAck, setLastClaimAck] = useState<
    ReviewClaimedAck | ReviewClaimFailed | null
  >(null);

  // Stash the latest onReviewUpdate in a ref so the socket subscription set
  // up in useEffect can call the latest version without re-running the
  // useEffect (which would tear down and rebuild the socket).
  const onReviewUpdateRef = useRef(onReviewUpdate);
  useEffect(() => {
    onReviewUpdateRef.current = onReviewUpdate;
  }, [onReviewUpdate]);

  useEffect(() => {
    if (!userId) return;

    setConnectionState("connecting");

    const socket = io({
      path: "/socket.io",
      transports: ["websocket", "polling"],
      auth: { userId },
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      setConnectionState("connected");
      // Re-use the existing qa:auth event so the server's authenticated-
      // socket bookkeeping (connectedUsers, socketTasks, socketReviewClaims)
      // populates exactly the same way it does for the QA flow.
      socket.emit("qa:auth", {
        userId,
        userName: userInfo?.userName ?? "",
        email: userInfo?.email ?? "",
      });
    });

    socket.on("disconnect", () => {
      setConnectionState("disconnected");
    });

    socket.on("connect_error", (error) => {
      console.error("[AnnotationReviewSocket] connect_error:", error);
      setConnectionState("error");
    });

    socket.on("review:update", (event: ReviewUpdateEvent) => {
      onReviewUpdateRef.current(event);
    });

    socket.on("review:claimed", (payload: ReviewClaimedAck) => {
      setLastClaimAck(payload);
    });

    socket.on("review:claim_failed", (payload: ReviewClaimFailed) => {
      setLastClaimAck(payload);
    });

    socket.on("review:error", (err: { message: string }) => {
      console.error("[AnnotationReviewSocket] review:error", err);
    });

    socket.on("review:released", (_payload: ReviewReleasedAck) => {
      // No-op locally. The broadcast review:update {type: "released"} will
      // also fire and the queue page will invalidate; the detail page
      // doesn't need to react to its own release ack.
    });

    // Heartbeat — same cadence as the QA hook. Drives the server's
    // connectedUsers freshness; no per-delivery emit needed because the
    // socket connection itself IS the heartbeat for claim ownership.
    const heartbeatInterval = setInterval(() => {
      if (socket.connected) socket.emit("qa:heartbeat");
    }, 30_000);

    return () => {
      clearInterval(heartbeatInterval);
      socket.disconnect();
      socketRef.current = null;
    };
    // userInfo intentionally excluded from deps — changing display name
    // mid-session shouldn't tear down the socket. The latest values are
    // captured via the qa:auth re-emit on the NEXT reconnect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const claimReview = useCallback(
    (deliveryId: string, opts?: { force?: boolean }) => {
      if (socketRef.current?.connected) {
        socketRef.current.emit("review:claim", { deliveryId, force: opts?.force });
      } else {
        console.warn(
          "[AnnotationReviewSocket] claimReview called while disconnected — drop on the floor; reconnect will not auto-claim",
        );
      }
    },
    [],
  );

  const releaseReview = useCallback((deliveryId: string) => {
    if (socketRef.current?.connected) {
      socketRef.current.emit("review:release", { deliveryId });
    }
    // Best-effort: if we're disconnected, the server-side disconnect
    // handler (Phase 2) is the backstop and will release any claim this
    // socket holds.
  }, []);

  return { connectionState, lastClaimAck, claimReview, releaseReview };
}

// Re-export the wire types so consumers don't have to chase the @shared
// import path themselves.
export type {
  ReviewClaim,
  ReviewClaimedAck,
  ReviewClaimFailed,
  ReviewReleasedAck,
  ReviewUpdateEvent,
};
